// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { countries } from '../data/countries';
import Country from '../model/Country';
import SignProcess from '../model/SignProcess';
import SignProcessCompany from '../model/SignProcessCompany';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
}));

type Props = {
  code: string,
  parent: SignProcess,
  process: SignProcessCompany,
  expanded: boolean,
  disabled: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (SignProcessCompany) => void,
};

const ProcessPersonalData = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { code, parent, process, expanded, disabled, onExpanded, onSave } = props;

  const validate = () => {
    return {};
  };

  const onSubmit = async (values, { setSubmitting }) => {
    let data = process;
    data.name = values.name;
    data.customerCompanyRol = values.customerCompanyRol;
    data.phone = values.phone;
    data.legalID = values.legalID;
    data.country = values.country ? values.country.label : '';
    data.province = values.province;
    data.city = values.city;
    data.address = values.address;
    data.zipCode = values.zipCode;
    onSave(data);
    setSubmitting(false);
  };

  let processStatusText = parent.completedCompanyData ? 'Completed' : 'Not completed';
  let processStatusIcon = parent.completedCompanyData ? <DoneIcon/> : <EditIcon color="secondary"/>;
  if (parent.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary"/>;
  }

  return (
    <Formik
      initialValues={{
        name: process.name,
        customerCompanyRol: process.customerCompanyRol,
        phone: process.phone,
        legalID: process.legalID || '',
        country: Country.find(countries, process.country),
        province: process.province || '',
        city: process.city || '',
        address: process.address || '',
        zipCode: process.zipCode || '',
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Accordion
            key={code}
            expanded={expanded}
            onChange={onExpanded(code)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls={`${code}-panel`}
              id={`${code}-panel`}
            >
              {processStatusIcon}&nbsp;
              <Typography className={classes.heading}>
                {t('Company data')}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {t(processStatusText)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    autoComplete="name"
                    name="name"
                    variant="outlined"
                    fullWidth
                    id="name"
                    label={t('Company name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <span className={classes.error}>
                    {errors.name && touched.name && errors.name}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="customerCompanyRol"
                    label={t('Company rol')}
                    name="customerCompanyRol"
                    autoComplete="customerCompanyRol"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.customerCompanyRol}
                  />
                  <span className={classes.error}>
                    {errors.customerCompanyRol && touched.customerCompanyRol && errors.customerCompanyRol}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="phone"
                    label={t('Phone')}
                    name="phone"
                    autoComplete="phone"
                    type="tel"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                  />
                  <span className={classes.error}>
                    {errors.phone && touched.phone && errors.phone}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="legalID"
                    label={t('Company ID')}
                    name="legalID"
                    autoComplete="legalID"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.legalID}
                  />
                  <span className={classes.error}>
                    {errors.legalID && touched.legalID && errors.legalID}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <Autocomplete
                    disabled={disabled}
                    fullWidth
                    id="country"
                    name="country"
                    options={countries}
                    autoHighlight
                    getOptionLabel={(option) => option.label}
                    value={values.country}
                    renderOption={(option) => (
                      <><span>{option.flag()}</span>&nbsp;{option.label}</>
                    )}
                    onChange={(e, value) => {
                      setFieldValue('country', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Country')}
                        variant="outlined"
                        onBlur={handleBlur}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'nothing-at-all', // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                  <span className={classes.error}>
                    {errors.country && touched.country && errors.country}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="province"
                    label={t('Province')}
                    name="province"
                    autoComplete="province"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.province}
                  />
                  <span className={classes.error}>
                    {errors.province && touched.province && errors.province}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="city"
                    label={t('City')}
                    name="city"
                    autoComplete="city"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.city}
                  />
                  <span className={classes.error}>
                    {errors.city && touched.city && errors.city}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="address"
                    label={t('Address')}
                    name="address"
                    autoComplete="address"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                  />
                  <span className={classes.error}>
                    {errors.address && touched.address && errors.address}
                  </span>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="zipCode"
                    label={t('Zip code')}
                    name="zipCode"
                    autoComplete="zipCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zipCode}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
            {!disabled && <>
              <Divider/>
              <AccordionActions>
                <Button
                  type="submit"
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  startIcon={disabled ? <CircularProgress color="secondary" size="1.2em"/> : null}
                >
                  {t('Save')}
                </Button>
              </AccordionActions>
            </>}
          </Accordion>
        </form>
      )}
    </Formik>
  );
};

export default ProcessPersonalData;
